/**
 * 发品/商品模板：类目属性里的「品牌名」选项来自店铺品牌缓存（bg.goods.brand.get），
 * 而非 attrs.get 的 values（平台通常不在类目属性里下发品牌枚举）。
 */
import { flattenBrandRows } from '@/tiktok-platform/utils/tiktokBrandIp';

/** 是否品牌类目属性（名称 / 英文名） */
export function isTiktokBrandAttribute(attr, rawAttr = null) {
  const name = [
    attr?.name,
    attr?.nameEn,
    rawAttr?.attribute_name,
    rawAttr?.attribute_name_cn,
    rawAttr?.attribute_name_en,
    rawAttr?.tiktok_prop?.name,
  ].map((s) => String(s || '').trim()).filter(Boolean).join(' ');
  if (!name) return false;
  if (/品牌/.test(name)) return true;
  return /\bbrands?\b/i.test(name);
}

/** 店铺品牌缓存 → AttributeField 选项（id 优先 vid，提交 productPropertyReqs.vid） */
export function shopBrandsToAttributeValues(brandsRaw) {
  return flattenBrandRows(brandsRaw)
    .filter((row) => row.brandCode && row.brandCode !== '—')
    .map((row) => {
      const vid = String(row.vid || row.brandCode).trim();
      const label = row.brandName && row.brandName !== '—'
        ? String(row.brandName).trim()
        : row.brandCode;
      return {
        id: vid,
        name: label,
        nameCn: label,
        brandId: row.brandCode,
      };
    })
    .filter((row) => row.id && row.name);
}

/** 店铺品牌缓存 → 发品页「品牌」下拉选项（value=brand_id，标签优先品牌名） */
export function shopBrandsToSelectOptions(brandsRaw) {
  return flattenBrandRows(brandsRaw)
    .filter((row) => row.brandCode && row.brandCode !== '—')
    .map((row) => {
      const id = String(row.brandCode).trim();
      const name = row.brandName && row.brandName !== '—' ? String(row.brandName).trim() : '';
      return { value: id, label: name ? `${name}（${id}）` : id };
    })
    .filter((row) => row.value);
}

function patchAttrList(list, brandAttrIds, brandValues) {
  return (Array.isArray(list) ? list : []).map((attr) => {
    if (!attr || !brandAttrIds.has(String(attr.attributeId))) return attr;
    return {
      ...attr,
      values: brandValues.map(({ id, name, nameCn }) => ({ id, name, nameCn })),
    };
  });
}

function resolveRawAttributeInfos(attrTpl) {
  const raw = attrTpl?.raw;
  if (!raw || typeof raw !== 'object') return [];
  if (Array.isArray(raw.data?.[0]?.attribute_infos)) return raw.data[0].attribute_infos;
  if (Array.isArray(raw.attribute_infos)) return raw.attribute_infos;
  if (Array.isArray(raw.data)) {
    const merged = [];
    raw.data.forEach((row) => {
      if (Array.isArray(row?.attribute_infos)) merged.push(...row.attribute_infos);
    });
    return merged;
  }
  return [];
}

/**
 * 将店铺品牌写入类目属性模板中的品牌字段（含 raw，供发品组装 vid）。
 * @returns {object} 新的 attrTpl（无品牌属性或无品牌数据时原样返回）
 */
export function applyShopBrandsToAttrTemplate(attrTpl, brandsRaw) {
  if (!attrTpl || typeof attrTpl !== 'object') return attrTpl;
  const brandValues = shopBrandsToAttributeValues(brandsRaw);
  if (!brandValues.length) return attrTpl;

  const rawInfos = resolveRawAttributeInfos(attrTpl);
  const brandAttrIds = new Set();

  (attrTpl.productAttrs || []).forEach((attr) => {
    const raw = rawInfos.find(
      (row) => String(row?.attribute_id ?? row?.attributeId) === String(attr?.attributeId),
    );
    if (isTiktokBrandAttribute(attr, raw)) {
      brandAttrIds.add(String(attr.attributeId));
    }
  });

  if (!brandAttrIds.size) {
    // 兜底：仅按展示名识别（raw 可能尚未带 tiktok_prop）
    (attrTpl.productAttrs || []).forEach((attr) => {
      if (isTiktokBrandAttribute(attr)) brandAttrIds.add(String(attr.attributeId));
    });
  }
  if (!brandAttrIds.size) return attrTpl;

  rawInfos.forEach((row) => {
    const id = String((row?.attribute_id ?? row?.attributeId) || '').trim();
    if (!id || !brandAttrIds.has(id)) return;
    row.attribute_value_info_list = brandValues.map((v) => ({
      attribute_value_id: v.id,
      attribute_value: v.name,
      attribute_value_name: v.name,
      attribute_value_en: v.name,
      tiktok_vid: v.id,
      tiktok_brand_id: v.brandId,
    }));
  });

  return {
    ...attrTpl,
    productAttrs: patchAttrList(attrTpl.productAttrs, brandAttrIds, brandValues),
    requiredProduct: patchAttrList(attrTpl.requiredProduct, brandAttrIds, brandValues),
    optionalProduct: patchAttrList(attrTpl.optionalProduct, brandAttrIds, brandValues),
    mainAttrs: patchAttrList(attrTpl.mainAttrs, brandAttrIds, brandValues),
    otherAttrs: patchAttrList(attrTpl.otherAttrs, brandAttrIds, brandValues),
  };
}
